import React from 'react';
import { TextField, Card, Typography, Autocomplete, Tooltip, Box } from '@mui/material';
import PropTypes from 'prop-types';
import EditIcon from '@mui/icons-material/Edit';
import SelectDropdown from '../../../components/SelectDropdown';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '../../../components/Button';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import styles from './styles';
import OfferExpiryDate from './OfferExpiryDate';
import { SWEETNER_DISCLAIMER } from '../constants';
import { useOfferSweetners } from '../hooks/useOfferSweetners';

const OfferSweetners = ({
  offersList = [],
  defaultExpiryDate,
  promotionData,
  setPromotionData,
  mode,
  isAdmin,
  loadedPromotionData,
  isDateSelectionDisabled,
}) => {
  const {
    // State values
    showDatePickerForOfferSweetnerID,
    setShowDatePickerForOfferSweetnerID,
    includeSweetnerOffer,
    configureHeadline,
    selectHeadline,
    disableConfig,
    customHeadline,
    customDisclaimer,
    promoExpiry,
    isSweetnerClickable,
    includeAppointmentDisclaimer,
    modifiedOfferData,
    isCardDisabled,
    isArchived,

    // Event handlers
    handleOfferInclude,
    handleHeadlineChange,
    handleCustomHeadline,
    handleCustomDisclaimer,
    handleAutocompleteChange,
    handleOfferExpiryDate,
    handleOfferExpiryDateForInstallation,
    handleScheduleExpDate,
    handlePurchaseExpDate,
    handleAppointmentDisInclude,
    toggleConfigureHeadline,
    renderModifiedHeadline,
  } = useOfferSweetners({
    offersList,
    defaultExpiryDate,
    promotionData,
    setPromotionData,
    mode,
    isAdmin,
    loadedPromotionData,
  });

  const sectionStyle =
    isArchived && !isAdmin
      ? { ...styles.sectionSpacing, paddingTop: '50px', ...styles.disableCard }
      : { ...styles.sectionSpacing, paddingTop: '50px' };

  const cardStyle = isCardDisabled
    ? { marginTop: '20px', ...styles.cardBox, ...styles.disableCard }
    : { marginTop: '20px', ...styles.cardBox };

  return (
    <section style={sectionStyle}>
      <Typography style={styles.heading}>Offer Sweeteners</Typography>
      {!isSweetnerClickable ? (
        <div style={{ position: 'relative', width: 'fit-content' }}>
          <Tooltip
            arrow
            placement="top"
            leaveDelay={500}
            sx={styles.freezeDateTooltip}
            title="Either Discount Offer or Finance Offer needs to be selected to include Sweetner Offer"
          >
            <FormGroup>
              <FormControlLabel control={<Checkbox checked={false} />} label="Include" />
            </FormGroup>
          </Tooltip>
        </div>
      ) : (
        <FormGroup sx={{ width: '100%' }}>
          <FormControlLabel
            control={<Checkbox checked={includeSweetnerOffer} onChange={handleOfferInclude} />}
            label="Include"
            sx={{ width: 'fit-content' }}
          />
        </FormGroup>
      )}

      <Card sx={cardStyle}>
        <div className="headline-dropdown" style={styles.divContainer}>
          <SelectDropdown
            label="Headline"
            options={modifiedOfferData || []}
            singleSelectDefaultValues=""
            handleFilterChange={handleHeadlineChange}
            updateSingleDropdownValue={selectHeadline}
            width={'-webkit-fill-available'}
            containerWidth={'65%'}
          />
          <Button
            sx={{ ...styles.buttonStyle, background: '#e0e0e0' }}
            disabled={disableConfig}
            startIcon={<EditIcon sx={{ paddingLeft: '5px' }} />}
            onClick={toggleConfigureHeadline}
          >
            Configure
          </Button>
        </div>
        {configureHeadline && (
          <div>
            {selectHeadline === 'custom_offer' ? (
              <div className="customHeadline" style={{ padding: '10px 20px' }}>
                <div>
                  <p style={{ ...styles.headingSmall, paddingTop: '5px' }}>
                    Headline: &nbsp;{' '}
                    <span style={{ background: 'rgb(255, 228, 51)', wordWrap: 'break-word' }}>
                      {customHeadline}
                    </span>
                  </p>
                  <TextField
                    variant="standard"
                    multiline
                    label="Type your own text"
                    value={customHeadline}
                    onChange={(event) => {
                      handleCustomHeadline(event.target.value);
                    }}
                    sx={{ width: '100%' }}
                  />
                </div>
                <div>
                  <p style={{ ...styles.headingSmall, paddingTop: '15px' }}>
                    Disclaimer: &nbsp;{' '}
                    <span style={{ background: 'rgb(255, 228, 51)', wordWrap: 'break-word' }}>
                      {customDisclaimer}
                    </span>
                  </p>
                  <TextField
                    variant="standard"
                    multiline
                    label="Type your own text (optional)"
                    value={customDisclaimer}
                    onChange={(event) => {
                      handleCustomDisclaimer(event.target.value);
                    }}
                    fullWidth
                  />
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', padding: '10px 20px', flexWrap: 'wrap' }}>
                <span style={{ ...styles.headingSmall, paddingTop: '5px' }}>Headline: &nbsp; </span>
                {renderModifiedHeadline(offersList, selectHeadline).map((part) => {
                  if (part.type === 'autocomplete') {
                    return (
                      <React.Fragment key={part.key}>
                        &nbsp;
                        <Autocomplete
                          sx={{
                            '& .MuiOutlinedInput-input': {
                              height: '20px',
                            },
                            '& .MuiAutocomplete-inputRoot': {
                              paddingTop: 0,
                              paddingBottom: 0,
                            },
                            margin: '2px 4px',
                          }}
                          disableClearable
                          options={part.options}
                          value={part.value}
                          onInputChange={(event, newInputValue) =>
                            handleAutocompleteChange(newInputValue, part.part)
                          }
                          freeSolo
                          renderInput={({ inputProps, params, ...rest }) => (
                            <Box sx={{ position: 'relative', bottom: '10px' }}>
                              <TextField
                                {...params}
                                type="number"
                                sx={{ ' & .MuiInputBase-root': { width: '100px' } }}
                                {...rest}
                                inputProps={{ ...inputProps, min: 0 }}
                                error={Number(inputProps.value) < 0}
                              />
                              {Number(inputProps.value) < 0 && (
                                <Typography
                                  variant="caption"
                                  color="error"
                                  component="div"
                                  sx={{ position: 'absolute' }}
                                >
                                  Value must be non-negative
                                </Typography>
                              )}
                            </Box>
                          )}
                        />
                        &nbsp;
                      </React.Fragment>
                    );
                  } else {
                    return (
                      <span
                        key={part.key}
                        style={{ ...styles.headingSmall, padding: '5px 1px 0 1px' }}
                      >
                        {part.text}
                      </span>
                    );
                  }
                })}
              </div>
            )}
          </div>
        )}
        <OfferExpiryDate
          showDatePickerForRegOfferID={showDatePickerForOfferSweetnerID}
          setShowDatePickerForRegOfferID={setShowDatePickerForOfferSweetnerID}
          handleOfferExpiryDate={handleOfferExpiryDate}
          handleOfferExpiryDateForInstallation={handleOfferExpiryDateForInstallation}
          handleScheduleExpDate={handleScheduleExpDate}
          handlePurchaseExpDate={handlePurchaseExpDate}
          promoExpiry={promoExpiry}
          offerApplicable={includeSweetnerOffer}
          defaultExpiryDate={defaultExpiryDate}
          promotionData={promotionData}
          isDateSelectionDisabled={isDateSelectionDisabled}
          disableConfig={disableConfig}
        />

        <div
          style={{
            padding: '0px 20px 20px 20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <Checkbox
              checked={includeAppointmentDisclaimer}
              onChange={handleAppointmentDisInclude}
              style={{ padding: '0px' }}
            />
            <span style={{ paddingLeft: '8px' }}>Include:</span>
            <p style={{ margin: '0px' }}>&nbsp;{SWEETNER_DISCLAIMER}</p>
          </div>
        </div>
      </Card>
    </section>
  );
};

// Complete PropTypes validation
OfferSweetners.propTypes = {
  offersList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      template: PropTypes.string,
      template_variables: PropTypes.arrayOf(PropTypes.string),
      nested_values: PropTypes.object,
    })
  ),
  defaultExpiryDate: PropTypes.instanceOf(Date),
  promotionData: PropTypes.shape({
    status: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    discount_offer: PropTypes.object,
    finance_offer: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    sweetener_offer: PropTypes.shape({
      headline: PropTypes.string,
      headline_builder_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      disclaimer: PropTypes.string,
      custom: PropTypes.bool,
      custom_values: PropTypes.object,
      initial_appointment_disclaimer: PropTypes.string,
      offer_expiry: PropTypes.shape({
        expiry_dates: PropTypes.arrayOf(PropTypes.string),
        offer_expiry_type: PropTypes.string,
      }),
    }),
  }),
  setPromotionData: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['Create', 'Edit']),
  isAdmin: PropTypes.bool,
  loadedPromotionData: PropTypes.bool,
  isDateSelectionDisabled: PropTypes.bool,
};

export default OfferSweetners;
